/**
 * Descriptor de Imóvel. Todos os campos de `ImovelOut`. O condomínio é referência; o endereço é uma
 * FK própria (mostrada em leitura por ora). O imóvel tem a figura do PROPRIETÁRIO (com histórico,
 * como o veículo), dos CONTRATOS DE ALUGUEL (com a flag `alugado`) e dos OCUPANTES (moradores ou
 * usuários comerciais). Painéis dedicados cobrem cada um; a ingestão por planilha traz imóveis em
 * massa com proprietário e condomínio correlacionados.
 */
import { Building } from 'lucide-react';
import { createElement } from 'react';
import { PainelContratosAluguel } from '@/components/crud/paineis/PainelContratosAluguel';
import { PainelDonosImovel } from '@/components/crud/paineis/PainelDonosImovel';
import { PainelOcupantes } from '@/components/crud/paineis/PainelOcupantes';
import type { ImovelCad } from '@/lib/api';
import { definirRecurso } from '@/lib/recursos/tipos';

const TIPO = [
  { valor: 'apartamento', rotulo: 'Apartamento' },
  { valor: 'casa', rotulo: 'Casa' },
  { valor: 'sala', rotulo: 'Sala' },
  { valor: 'loja', rotulo: 'Loja' },
  { valor: 'terreno', rotulo: 'Terreno' },
  { valor: 'galpao', rotulo: 'Galpão' },
  { valor: 'vaga', rotulo: 'Vaga' },
];
const FINALIDADE = [
  { valor: 'residencial', rotulo: 'Residencial' },
  { valor: 'comercial', rotulo: 'Comercial' },
  { valor: 'misto', rotulo: 'Misto' },
  { valor: 'industrial', rotulo: 'Industrial' },
  { valor: 'rural', rotulo: 'Rural' },
];

export const imovel = definirRecurso<ImovelCad>({
  chave: 'imoveis',
  endpoint: '/cadastro/imoveis',
  ownerTipo: 'imovel',
  titulo: 'Imóveis',
  tituloSingular: 'imóvel',
  descricao: 'Imóveis — matrícula, IPTU, condomínio, proprietários, locação e ocupantes.',
  icone: Building,
  capacidades: { podeEditar: true, podeExcluir: true },
  buscaPlaceholder: 'Buscar por identificação ou matrícula',
  rotuloItem: (i) => i.identificacao || i.matricula || 'Imóvel',

  colunas: [
    { chave: 'identificacao', rotulo: 'Identificação' },
    { chave: 'tipo', rotulo: 'Tipo', ocultarEm: 'sm' },
    { chave: 'matricula', rotulo: 'Matrícula', mono: true },
  ],

  camposCriar: [
    { chave: 'identificacao', rotulo: 'Identificação', tipo: 'texto' },
    { chave: 'matricula', rotulo: 'Matrícula', tipo: 'texto' },
  ],

  importarPlanilha: {
    endpoint: '/cadastro/ingestao/imoveis',
    descricao:
      'Suba um Excel/CSV de imóveis. A IA sugere o mapa coluna→campo; o proprietário (pessoa/empresa ' +
      'por documento) é registrado como dono e o condomínio (nome/CNPJ) buscado no banco ou criado. ' +
      'Você revisa antes de importar. Dedup pela matrícula.',
  },

  gruposEdicao: [
    {
      id: 'identificacao',
      titulo: 'Identificação',
      campos: [
        { chave: 'identificacao', rotulo: 'Identificação', tipo: 'texto', colSpan: 2 },
        { chave: 'tipo', rotulo: 'Tipo', tipo: 'select', opcoes: TIPO },
        { chave: 'finalidade', rotulo: 'Finalidade', tipo: 'select', opcoes: FINALIDADE },
        { chave: 'matricula', rotulo: 'Matrícula', tipo: 'texto' },
        { chave: 'cartorio', rotulo: 'Cartório / comarca', tipo: 'texto' },
        { chave: 'inscricao_iptu', rotulo: 'Inscrição IPTU', tipo: 'texto' },
        {
          chave: 'condominio_id',
          rotulo: 'Condomínio',
          tipo: 'referencia',
          refEndpoint: '/cadastro/condominios',
        },
        { chave: 'endereco_id', rotulo: 'Endereço (id)', tipo: 'leitura' },
      ],
    },
    {
      id: 'caracteristicas',
      titulo: 'Características',
      campos: [
        { chave: 'area_m2', rotulo: 'Área (m²)', tipo: 'numero' },
        { chave: 'fracao_ideal', rotulo: 'Fração ideal', tipo: 'numero' },
        { chave: 'quartos', rotulo: 'Quartos', tipo: 'numero' },
        { chave: 'vagas', rotulo: 'Vagas', tipo: 'numero' },
      ],
    },
    {
      id: 'valores',
      titulo: 'Valores e locação',
      campos: [
        { chave: 'valor_venal', rotulo: 'Valor venal (R$)', tipo: 'numero' },
        { chave: 'valor_mercado', rotulo: 'Valor de mercado (R$)', tipo: 'numero' },
        { chave: 'valor_aluguel', rotulo: 'Valor do aluguel (R$)', tipo: 'numero' },
        // `alugado` é derivado do contrato vigente — só leitura (os serviços mantêm em sincronia).
        { chave: 'alugado', rotulo: 'Alugado', tipo: 'leitura' },
      ],
    },
    {
      id: 'sistema',
      titulo: 'Situação e observações',
      campos: [
        { chave: 'ativo', rotulo: 'Ativo', tipo: 'booleano' },
        { chave: 'observacoes', rotulo: 'Observações', tipo: 'texto-longo', colSpan: 2 },
        { chave: 'created_at', rotulo: 'Criado em', tipo: 'leitura' },
      ],
    },
  ],

  paineis: [
    {
      tipo: 'custom',
      id: 'donos-imovel',
      titulo: 'Proprietários',
      render: ({ item }) => createElement(PainelDonosImovel, { imovelId: item.id }),
    },
    { tipo: 'documentos' },
    { tipo: 'dossie' },
  ],

  // Imóvel não é dono de contatos (o endereço é FK no próprio registro); as abas cobrem dados,
  // proprietários (histórico), locação (contratos), ocupantes, documentos e a correlação.
  abas: [
    {
      id: 'dados',
      rotulo: 'Dados do Imóvel',
      grupos: ['identificacao', 'caracteristicas', 'valores', 'sistema'],
    },
    {
      id: 'proprietarios',
      rotulo: 'Proprietários',
      // Registro de propriedade com histórico e co-propriedade (fração ideal).
      paineis: [
        {
          tipo: 'custom',
          id: 'donos-imovel',
          titulo: 'Proprietários',
          render: ({ item }) => createElement(PainelDonosImovel, { imovelId: item.id }),
        },
      ],
    },
    {
      id: 'locacao',
      rotulo: 'Locação',
      // Contratos de aluguel: abrir marca `alugado` e recarrega o item para refletir a flag.
      paineis: [
        {
          tipo: 'custom',
          id: 'contratos-aluguel',
          titulo: 'Contratos de aluguel',
          render: ({ item, recarregar }) =>
            createElement(PainelContratosAluguel, { imovelId: item.id, onMudou: recarregar }),
        },
      ],
    },
    {
      id: 'ocupantes',
      rotulo: 'Ocupantes',
      // Quem ocupa (morador ou comercial), ligado ou não a um contrato.
      paineis: [
        {
          tipo: 'custom',
          id: 'ocupantes',
          titulo: 'Ocupantes',
          render: ({ item }) => createElement(PainelOcupantes, { imovelId: item.id }),
        },
      ],
    },
    { id: 'documentos', rotulo: 'Documentos', paineis: [{ tipo: 'documentos' }] },
    { id: 'dossie', rotulo: 'Dossiê', paineis: [{ tipo: 'dossie' }] },
    { id: 'grafo', rotulo: 'Grafo', paineis: [{ tipo: 'grafo' }] },
  ],
});
